import { useEffect, useRef, useState } from 'react';

const isDev = process.env.NODE_ENV === 'development';

const hasNotificationPermission = () => 'Notification' in window && Notification.permission === 'granted';

interface UsePushNotificationsOptions {
  /** Register this browser with FCM (asks permission first); turn on once a user who should get notifications is logged in */
  enabled: boolean;
  /**
   * Show notifications that arrive while Classroom is open and in front (Firebase only shows them by itself
   * while Classroom is in the background or closed)
   */
  displayEnabled: boolean;
  /** Called with this browser's Firebase Installation ID (FID) when registered, and again if it changes */
  onRegistered?: (fid: string) => void;
  /** Called with the FID when it is no longer registered */
  onUnregistered?: (fid: string) => void;
  /** Called with the user's answer to the browser's notification prompt: true = allowed */
  onPermission?: (allowed: boolean) => void;
}

/**
 * Web push for Classroom:
 * - while enabled: asks for permission and registers this browser with FCM (FID-based)
 * - while displayEnabled: shows notifications that arrive while Classroom is open
 * Firebase Messaging is loaded on demand, so it isn't part of the main bundle for users who never use push.
 */
export const usePushNotifications = ({
  enabled,
  displayEnabled,
  onRegistered,
  onUnregistered,
  onPermission,
}: UsePushNotificationsOptions) => {
  const handlersRef = useRef({ onRegistered, onUnregistered, onPermission });
  handlersRef.current = { onRegistered, onUnregistered, onPermission };
  const [isPermissionGranted, setIsPermissionGranted] = useState(hasNotificationPermission);

  // While Classroom is open and focused, Firebase passes messages to the page instead of showing them,
  // so the page shows them (logged in or not, as long as this browser allows notifications)
  useEffect(() => {
    if (!displayEnabled || !isPermissionGranted) return undefined;
    let isCancelled = false;
    let stopListening: (() => void) | undefined;

    import('./messaging')
      .then(async ({ isPushSupported, listenForForegroundMessages }) => {
        if (isCancelled || !(await isPushSupported())) return;
        stopListening = listenForForegroundMessages();
      })
      .catch((error) => {
        if (isDev) console.debug('[Push] listener setup failed', error);
      });

    return () => {
      isCancelled = true;
      stopListening?.();
    };
  }, [displayEnabled, isPermissionGranted]);

  // Registration: only while a user who should get notifications is logged in. Logging out doesn't unregister,
  // so notifications keep arriving after logout.
  useEffect(() => {
    if (!enabled) return undefined;
    let isCancelled = false;
    let stopRegistration: (() => void) | null = null;

    import('./messaging')
      .then(({ startPushRegistration }) =>
        startPushRegistration({
          onRegistered: (fid) => {
            // Copy the FID from the console to send a test message (Admin SDK: message.fid)
            if (isDev) console.debug('[Push] registered FID', fid);
            handlersRef.current.onRegistered?.(fid);
          },
          onUnregistered: (fid) => {
            if (isDev) console.debug('[Push] unregistered FID', fid);
            handlersRef.current.onUnregistered?.(fid);
          },
          onPermission: (allowed) => {
            setIsPermissionGranted(allowed);
            handlersRef.current.onPermission?.(allowed);
          },
        }).then((stop) => {
          if (isCancelled) {
            stop?.();
            return;
          }
          stopRegistration = stop;
        }),
      )
      .catch((error) => {
        if (isDev) console.debug('[Push] setup failed', error);
      });

    return () => {
      isCancelled = true;
      stopRegistration?.();
    };
  }, [enabled]);
};
